import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authService } from "@services/api";

const AuthContext = createContext({});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const clearAuthState = useCallback(() => {
    localStorage.removeItem("empresaAtivaId");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Verificar se usuário está logado ao carregar a aplicação
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        const userData = localStorage.getItem("user");

        if (token && userData) {
          if (isTokenValid(token)) {
            setUser(JSON.parse(userData));
            setIsAuthenticated(true);
          } else {
            clearAuthState();
          }
        }
      } catch (error) {
        console.error("Erro ao inicializar autenticação:", error);
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [clearAuthState]);

  // Função para fazer login
  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authService.login(credentials);

      if (response.data.success) {
        const { token, user: userData } = response.data.data;

        // Salvar no localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));

        // Atualizar estado
        setUser(userData);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      } else {
        throw new Error(response.data.error || "Erro no login");
      }
    } catch (error) {
      const status = error.status || error.response?.status || null;
      const errorMessage =
        error.response?.data?.error || error.message || "Erro ao fazer login";
      return { success: false, error: errorMessage, status };
    } finally {
      setLoading(false);
    }
  };

  // Função para fazer logout
  const logout = useCallback(async () => {
    try {
      // Tentar notificar o servidor (opcional)
      if (isAuthenticated) {
        await authService.logout();
      }
    } catch (error) {
      // Ignorar erros no logout do servidor
      console.warn("Erro ao fazer logout no servidor:", error);
    } finally {
      clearAuthState();
    }
  }, [clearAuthState, isAuthenticated]);

  // Função para atualizar perfil
  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);

      if (response.data.success) {
        const updatedUser = response.data.data;

        // Atualizar dados locais
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);

        return { success: true, user: updatedUser };
      } else {
        throw new Error(response.data.error || "Erro ao atualizar perfil");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Erro ao atualizar perfil";
      return { success: false, error: errorMessage };
    }
  };

  // Função para alterar senha
  const changePassword = async (passwordData) => {
    try {
      const response = await authService.changePassword(passwordData);

      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        throw new Error(response.data.error || "Erro ao alterar senha");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.message || "Erro ao alterar senha";
      return { success: false, error: errorMessage };
    }
  };

  const uploadAvatar = async (file) => {
    try {
      const response = await authService.uploadAvatar(file);

      if (response.data.success) {
        const updatedUser = response.data.data;
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      } else {
        throw new Error(response.data.error || "Erro ao atualizar avatar");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Erro ao atualizar avatar";
      return { success: false, error: errorMessage };
    }
  };

  const deleteAvatar = async () => {
    try {
      const response = await authService.deleteAvatar();

      if (response.data.success) {
        const updatedUser = response.data.data;
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      } else {
        throw new Error(response.data.error || "Erro ao remover avatar");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Erro ao remover avatar";
      return { success: false, error: errorMessage };
    }
  };

  // Função para renovar token
  const refreshToken = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;

      const response = await authService.refresh(token);

      if (response.data.success) {
        const newToken = response.data.data.token;
        localStorage.setItem("token", newToken);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Erro ao renovar token:", error);
      clearAuthState();
      return false;
    }
  };

  // Verificar se token é válido
  const isTokenValid = (token) => {
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;

      // Verificar se o token expira em menos de 5 minutos
      return payload.exp > currentTime + 300; // 5 minutos
    } catch (error) {
      console.error("Token inválido:", error);
      return false;
    }
  };

  // Verificar permissões
  const hasPermission = (requiredRole) => {
    if (!user || !isAuthenticated) return false;

    if (requiredRole === "admin") {
      return user.role === "admin";
    }

    return true; // Usuários autenticados têm permissão básica
  };

  // Verificar se é admin
  const isAdmin = () => {
    return user?.role === "admin";
  };

  // Verificar se é usuário comum
  const isUser = () => {
    return user?.role === "user";
  };

  const value = {
    // Estado
    user,
    loading,
    isAuthenticated,

    // Funções
    login,
    logout,
    updateProfile,
    changePassword,
    uploadAvatar,
    deleteAvatar,
    refreshToken,
    hasPermission,
    isAdmin,
    isUser,

    // Dados do usuário
    userName: user?.name || "",
    userEmail: user?.email || "",
    userRole: user?.role || "",
    userId: user?.id || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
